import { useState, useRef, useCallback, lazy, Suspense, type ReactNode } from 'react'
import { AuthProvider, useAuth } from '@/lib/auth'
import { NavProvider, type SectionId } from '@/lib/nav'
import AuthPage from '@/pages/Auth'
import Accueil from '@/pages/Accueil'
import BandeauRetourAbonnement from '@/components/BandeauRetourAbonnement'
import MurAbonnement from '@/components/MurAbonnement'
import { accesActif } from '@shared/calculs/abonnement'
import type { BrouillonFicheCosmetique } from '@/pages/fiche-cosmetique/versFicheCosmetique'
import { fetchFicheCosmetique, type FicheCosmetiqueDetail } from '@/lib/fichesCosmetique'

// Pages chargées À LA DEMANDE (code-splitting) : chacune n'est téléchargée qu'à sa première
// ouverture, ce qui allège le tout premier chargement de l'app et sort les grosses dépendances
// (génération PDF Factur-X côté Factures, scan code-barres côté Devis) du bundle initial. Les
// écrans d'entrée (Auth, Accueil) et les composants de structure restent immédiats pour un
// démarrage instantané.
const Formulateur = lazy(() => import('@/pages/Formulateur'))
const MesIngredients = lazy(() => import('@/pages/MesIngredients'))
const MesArticles = lazy(() => import('@/pages/MesArticles'))
const MesStock = lazy(() => import('@/pages/MesStock'))
const MesClients = lazy(() => import('@/pages/MesClients'))
const MesDevis = lazy(() => import('@/pages/MesDevis'))
const MesFactures = lazy(() => import('@/pages/MesFactures'))
const MesDepots = lazy(() => import('@/pages/MesDepots'))
const Reglages = lazy(() => import('@/pages/Reglages'))
const ComptesClientes = lazy(() => import('@/pages/admin/ComptesClientes'))
const FichesCosmetique = lazy(() => import('@/pages/FichesCosmetique'))
const FabricationCosmetiqueForm = lazy(() => import('@/pages/fiche-cosmetique/FabricationCosmetiqueForm'))
const FicheCosmetiqueImpression = lazy(() => import('@/pages/fiche-cosmetique/FicheCosmetiqueImpression'))

// Écran d'attente affiché pendant le chargement (statut auth en cours, ou page à la demande).
function EcranChargement() {
  return (
    <div className="min-h-screen bg-bdspBg flex items-center justify-center">
      <p className="text-stone-500">Chargement…</p>
    </div>
  )
}

// Écran courant : une section du menu, ou un sous-écran contextuel des fiches.
type Page =
  | SectionId
  | 'fabrication-cosmetique'
  | 'fiche-cosmetique-edit'
  | 'fiche-cosmetique-impression'

// Section à mettre en évidence dans le menu pour un écran donné (les sous-écrans
// de fiche restent rattachés à la section « Fiches » ; la fabrication cosmétique à
// « Formulateur », d'où elle est lancée).
function sectionDeLaPage(page: Page): SectionId {
  // Création d'une fabrication : lancée depuis le formulateur -> on garde « Formulateur » actif.
  if (page === 'fabrication-cosmetique') return 'formulateur'
  // Édition d'une fiche existante : on vient de « Lots de production ».
  if (page === 'fiche-cosmetique-edit') return 'fiches'
  if (page === 'fiche-cosmetique-impression') return 'fiches'
  return page
}

function Shell() {
  const { status, user } = useAuth()
  const [page, setPage] = useState<Page>('accueil')
  const [brouillonCosmetique, setBrouillonCosmetique] = useState<BrouillonFicheCosmetique | null>(
    null,
  )
  // Fiche cosmétique chargée pour réouverture / édition (depuis « Lots de production »).
  const [ficheCosmetique, setFicheCosmetique] = useState<FicheCosmetiqueDetail | null>(null)
  // Id de la fiche à imprimer (vue A4 dédiée, ouverte depuis « Lots de production »).
  const [ficheImpressionId, setFicheImpressionId] = useState<string | null>(null)
  // Brouillon de facture à ouvrir directement en édition (après conversion d'un devis).
  const [factureInitiale, setFactureInitiale] = useState<string | null>(null)

  // Garde de navigation : un écran (le calculateur) peut enregistrer une fonction
  // qui renvoie false pour ANNULER un départ (ex. modifications non enregistrées).
  const gardeNavigationRef = useRef<(() => boolean) | null>(null)
  const definirGardeNavigation = useCallback((garde: (() => boolean) | null) => {
    gardeNavigationRef.current = garde
  }, [])

  // Va vers une section du menu. Respecte le garde de navigation (peut annuler le départ).
  const naviguer = (section: SectionId) => {
    if (gardeNavigationRef.current && !gardeNavigationRef.current()) return
    setPage(section)
  }

  // Ouvre une fiche cosmétique existante en édition : charge le détail puis affiche le formulaire.
  const ouvrirFicheCosmetique = async (id: string) => {
    const fiche = await fetchFicheCosmetique(id)
    setBrouillonCosmetique(null)
    setFicheCosmetique(fiche)
    setPage('fiche-cosmetique-edit')
  }

  if (status === 'loading') {
    return <EcranChargement />
  }

  if (status !== 'authenticated') return <AuthPage />

  // Mur d'accès (brique S3) : si l'accès n'est plus actif (essai terminé / abonnement à
  // renouveler), on ne laisse ouvert que Réglages (pour s'abonner). La sécurité réelle est
  // côté serveur (middlewareAcces) ; ceci est l'équivalent visuel.
  if (user && !accesActif(user) && page !== 'reglages') {
    return (
      <NavProvider
        value={{ sectionCourante: sectionDeLaPage(page), naviguer, definirGardeNavigation }}
      >
        <BandeauRetourAbonnement />
        <MurAbonnement onSabonner={() => naviguer('reglages')} />
      </NavProvider>
    )
  }

  let contenu: ReactNode
  if (page === 'formulateur')
    contenu = (
      <Formulateur
        onRetour={() => naviguer('accueil')}
        onFabriquer={(b) => {
          setBrouillonCosmetique(b)
          setPage('fabrication-cosmetique')
        }}
      />
    )
  else if (page === 'ingredients')
    contenu = <MesIngredients onRetour={() => setPage('accueil')} />
  else if (page === 'articles')
    contenu = <MesArticles onRetour={() => setPage('accueil')} />
  else if (page === 'stock')
    contenu = <MesStock onRetour={() => setPage('accueil')} />
  else if (page === 'clients')
    contenu = <MesClients onRetour={() => setPage('accueil')} />
  else if (page === 'devis')
    contenu = (
      <MesDevis
        onRetour={() => setPage('accueil')}
        onConvertiEnFacture={(factureId) => {
          setFactureInitiale(factureId)
          setPage('factures')
        }}
      />
    )
  else if (page === 'factures')
    contenu = (
      <MesFactures
        onRetour={() => setPage('accueil')}
        factureInitialeId={factureInitiale}
        onInitialeConsommee={() => setFactureInitiale(null)}
      />
    )
  else if (page === 'depots')
    contenu = (
      <MesDepots
        onRetour={() => setPage('accueil')}
        onFactureCreee={(factureId) => {
          setFactureInitiale(factureId)
          setPage('factures')
        }}
      />
    )
  else if (page === 'reglages')
    contenu = <Reglages onRetour={() => setPage('accueil')} />
  else if (page === 'comptes')
    contenu = <ComptesClientes onRetour={() => setPage('accueil')} />
  else if (page === 'fiches')
    contenu = (
      <FichesCosmetique
        onRetour={() => setPage('accueil')}
        onOuvrir={(id) => void ouvrirFicheCosmetique(id)}
        onImprimer={(id) => {
          setFicheImpressionId(id)
          setPage('fiche-cosmetique-impression')
        }}
      />
    )
  else if (page === 'fiche-cosmetique-impression' && ficheImpressionId)
    contenu = (
      <FicheCosmetiqueImpression
        id={ficheImpressionId}
        onRetour={() => setPage('fiches')}
      />
    )
  else if (page === 'fabrication-cosmetique' && brouillonCosmetique)
    contenu = (
      <FabricationCosmetiqueForm
        brouillon={brouillonCosmetique}
        onRetour={() => setPage('formulateur')}
        onEnregistre={() => setPage('formulateur')}
      />
    )
  else if (page === 'fiche-cosmetique-edit' && ficheCosmetique)
    contenu = (
      <FabricationCosmetiqueForm
        // La key inclut le statut : après une annulation de libération, la fiche rechargée
        // repasse « en cours » -> la key change -> remontage propre (états réinitialisés).
        key={`${ficheCosmetique.id}-${ficheCosmetique.statut}`}
        fiche={ficheCosmetique}
        onRetour={() => setPage('fiches')}
        onEnregistre={() => setPage('fiches')}
        onRouvrir={(id) => void ouvrirFicheCosmetique(id)}
      />
    )
  else
    contenu = (
      <Accueil
        onOuvrirFormulateur={() => naviguer('formulateur')}
        onOuvrirIngredients={() => naviguer('ingredients')}
        onOuvrirArticles={() => naviguer('articles')}
        onOuvrirStock={() => naviguer('stock')}
        onOuvrirClients={() => naviguer('clients')}
        onOuvrirDevis={() => naviguer('devis')}
        onOuvrirFactures={() => naviguer('factures')}
        onOuvrirDepots={() => naviguer('depots')}
        onOuvrirFiches={() => naviguer('fiches')}
        onOuvrirReglages={() => naviguer('reglages')}
      />
    )

  return (
    <NavProvider
      value={{ sectionCourante: sectionDeLaPage(page), naviguer, definirGardeNavigation }}
    >
      <BandeauRetourAbonnement />
      <Suspense fallback={<EcranChargement />}>{contenu}</Suspense>
    </NavProvider>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <Shell />
    </AuthProvider>
  )
}
